import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '../../../setup';
import { getTestRunsByWebsiteId } from '../../modules/auth/redux/TestRunCRUD';
import { TestRunByWebsite } from '../../modules/auth/models/TestRunModel';
import _ from 'lodash';
import Pagination from '../layout/Pagination';
import { SortedTh } from '../layout/MasterLayout';
import { cleanString } from '../utils';

const getScoreColor = (score: number) => {
    if (score > 89) {
        return 'green';
    } else if (score > 39) {
        return 'orange';
    }
    return 'red';
}

const ManageTestRuns: React.FC = () => {
    const { websiteId, pageNo = 1 }: any = useParams();
    const { pathname } = useLocation();
    const history = useHistory();
    const [data, setData] = useState<TestRunByWebsite>();
    const [isLoading, setIsLoading] = useState(false);
    const [dataError, setDataError] = useState<{ error: string }>();
    const [testMode, setTestModeFilter] = useState<string>('-1');
    const [wcagVersion, setWcagVersionFilter] = useState<string>('-1');
    const [wcagLevel, setWcagLevelFilter] = useState<string>('-1');
    const [status, setStatusFilter] = useState<string | undefined>('-1');
    const [sortColumn, setSortColumn] = useState<string | undefined>('created');
    const [sortDirection, setSortDirection] = useState<string | undefined>('desc');
    const limit = 10;
    let pgNo = parseInt(pageNo);
    if (pgNo < 1 || Number.isNaN(pgNo)) {
        pgNo = 1
    }

    const fetchData = () => {
        const where: any = {};
        for (const whereCnd of [{ testMode }, { wcagVersion }, { wcagLevel }, { status }, { websiteId }]) {
            const key = Object.keys(whereCnd)[0];
            const value = (whereCnd as any)[key];
            if (value && value != '-1') {
                where[key] = value;
            }
        }
        const filter: any = { where, limit: limit + 1, skip: (pgNo - 1) * limit };
        if (sortDirection) {
            filter.order = `${sortColumn} ${sortDirection.toUpperCase()}`;
        }
        setIsLoading(true);
        getTestRunsByWebsiteId(websiteId, filter).then((result) => {
            setData(result.data);
            setDataError(undefined);
        })
            .catch(e => setDataError({ error: "Server Error! Cannot fetch data!" }))
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        return history.listen((location) => {
            setIsLoading(true);
        })
    }, [history]);

    useEffect(() => {
        fetchData();
    }, [websiteId, pgNo, sortDirection]);

    if (isLoading) {
        return (
            <div>
                <br />
                <span>Loading... </span> <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </div>
        );
    }

    if (dataError || data == null) {
        return (
            <div className='card mb-5 mb-xl-10'>
                <div
                    className='card-header border-0'
                >
                    {dataError ? (
                        <div>
                            <br />
                            <div className='mb-lg-15 alert alert-danger d-flex flex-column-fluid flex-center'>
                                <div className='alert-text font-weight-bold'>{dataError.error}</div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <br />
                            <span>Loading... </span> <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className='test-runsfor'>
            <div className="bredcrumb-cnntr">
                <ul>
                    <li><Link to={`/v2/dashboard`}>Home</Link></li>
                    <li>
                        <Link to={`/v2/core/website/manage`}>
                            Manage Websites
                        </Link>
                    </li>
                    <li>View Tests</li>
                </ul>
            </div>
            <div className="site-head">
                <h1 className="main-title">Test Runs For</h1>
            </div>
            <div className="site-details-widgt">
                <div>
                    <div className="site-details">
                        <a className="h2 underline" href={data.website.url} target='_blank'
                            onClick={(e) => !window.confirm('You are being redirected to an external website. Please note that SugamyaWeb cannot be held responsible for external websites content & privacy policies.') && e.preventDefault()}
                            title={`external site ${cleanString(data.website.name)} that opens in a new window`}><strong>{cleanString(data.website.name)}</strong></a><br />
                        <p>Organization: {cleanString(data.website.department?.name)}</p>
                    </div>
                    <ul className="site-info">
                        <li><strong className="h2">{data.counts.completeCnt + data.counts.failedCnt + data.counts.partiallyCompleteCnt}</strong> Total Test Done</li>
                        <li><strong className="h2 green">{data.counts.completeCnt}</strong> Complete</li>
                        <li><strong className="h2 orange">{data.counts.partiallyCompleteCnt}</strong> Partially Complete</li>
                        <li><strong className="h2 red">{data.counts.failedCnt}</strong> Failure</li>

                    </ul>
                </div>
            </div>
            <div className="project-list table-filter">
                <form>
                    <select className="single-page"
                        aria-label='select test mode'
                        onChange={e => setTestModeFilter(e.target.value)}>
                        <option value='-1'>All Test Modes</option>
                        <option value='single_page'>Single Page</option>
                        <option value='full_site'>Full Site</option>
                    </select>
                    <select className="wcag2"
                        aria-label='select WCAG version'
                        onChange={e => setWcagVersionFilter(e.target.value)}>
                        <option value='-1'>All WCAG Versions</option>
                        <option value='WCAG 2.0 without ARIA'>WCAG 2.0 without ARIA</option>
                        <option value='WCAG 2.1 without ARIA'>WCAG 2.1 without ARIA</option>
                        <option value='WCAG 2.0'>WCAG 2.0</option>
                        <option value='WCAG 2.1'>WCAG 2.1</option>
                    </select>
                    <select className="wcag-level"
                        aria-label='select WCAG level'
                        onChange={e => setWcagLevelFilter(e.target.value)}>
                        <option value='-1'>All WCAG Levels</option>
                        <option value='Level A'>Level A</option>
                        <option value='Level AA'>Level AA</option>
                        <option value='Level AAA'>Level AAA</option>
                    </select>
                    <select className="partially-comp"
                        aria-label='select status'
                        onChange={e => setStatusFilter(e.target.value)}>
                        <option value='-1'>All Statuses</option>
                        <option value='PENDING'>PENDING</option>
                        <option value='RUNNING'>RUNNING</option>
                        <option value='PARTIALLY COMPLETE'>PARTIALLY COMPLETE</option>
                        <option value='COMPLETE'>COMPLETE</option>
                        <option value='FAILURE'>FAILURE</option>
                    </select>
                    <button className="btn" onClick={(e) => { fetchData(); e.preventDefault(); }}>Filter</button>
                </form>
            </div>
            <div className="project-list">
                <table className="weblist-table dataTable">
                    <thead>
                        <tr>
                            <th scope="col" className="single-page">Test Mode</th>
                            <th scope="col">WCAG Version</th>
                            <th scope="col">WCAG Level</th>
                            <SortedTh fieldName={'created'}
                                sortColumn={sortColumn}
                                setSortColumn={setSortColumn}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}>Run Date/Time</SortedTh>
                            <SortedTh fieldName={'status'}
                                sortColumn={sortColumn}
                                setSortColumn={setSortColumn}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}>Status</SortedTh>
                            <SortedTh fieldName={'score'}
                                sortColumn={sortColumn}
                                setSortColumn={setSortColumn}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}>Latest Score</SortedTh>
                            <th scope="col" className="action">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.testRuns.slice(0, limit).map(run => {
                            const created = run.created ? moment(run.created).tz('Asia/Kolkata') : null;
                            return (
                                <tr>
                                    <td>{_.startCase(run.testMode)}</td>
                                    <td>{run.wcagVersion}</td>
                                    <td>{run.wcagLevel}</td>
                                    <td>{created ? <><strong>{created.format('DD MMM YYYY')}</strong><br />{created.format('h:mm:ss A')}</> : null}</td>
                                    <td>
                                        {run.status == 'FAILURE' ? <img src="/assetsv2/images/Failure.svg" alt="Delete" className="del-icon" /> : null}
                                        {run.status == 'RUNNING' ? <img src="/assetsv2/images/running.svg" alt="Delete" className="del-icon" /> : null}
                                        {run.status == 'PENDING' ? <img src="/assetsv2/images/pending.svg" alt="Delete" className="del-icon" /> : null}
                                        {run.status == 'COMPLETE' ? <img src="/assetsv2/images/Success.svg" alt="Success" /> : null}
                                        {run.status == 'PARTIALLY COMPLETE' ? <img src="/assetsv2/images/Success.svg" alt="Success" /> : null}
                                        {/* TODO: other run status icons*/}
                                        <span> {run.status}</span>
                                    </td>
                                    <td>{run.score ? <strong className={getScoreColor(run.score)}>{run.score}</strong> : null}</td>
                                    <td className="view-box">
                                        <Link to={`/v2/core/test/${run.id}/overview`} className="btn">View Test Report</Link>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            {data.testRuns.length == 0 ? <div style={{ textAlign: 'center', marginTop: 5 }}><strong>No data to display</strong></div> : null}
            <nav aria-label="Page navigation" className="pagination-wrap">
                {data?.testRuns ?
                    <Pagination
                        currentPage={pgNo}
                        totalCount={data.testRuns.length}
                        pageSize={limit}
                        infinite={true}
                        onPageChange={(page: number) => history.push(`/v2/core/website/${websiteId}/testruns/${page}`)}
                    /> : null}
            </nav>
        </div>
    )
}

export { ManageTestRuns }
